import {
  ClaimMethod,
  type ClaimRequestViewDto,
  Permission,
} from '@up2stay/up2promo-api-client';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useClaimErrorMessage } from '@/core/components/products/claim-requests-panel';
import {
  useClaimDict,
  useClaimRequestActions,
  useClaimRequests,
  useNow,
} from '@/core/hooks/use-claim-requests';
import { useIntercom } from '@/core/hooks/use-intercom';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  findPendingClaimForHash,
  readClaimApiError,
  type ClaimApiError,
} from '@/core/lib/claim-requests';
import type { ProductHashResolution } from '@/core/lib/product-hash-resolution';
import {
  ClaimOutcomeAlreadyYours,
  ClaimOutcomeAssignedToOtherUser,
  ClaimOutcomeCantClaim,
  ClaimOutcomeChecking,
  ClaimOutcomeEmailSent,
  ClaimOutcomeError,
  ClaimOutcomeInlineError,
  ClaimOutcomeManualForm,
  ClaimOutcomeNoPermission,
  ClaimOutcomePendingEmail,
  ClaimOutcomePendingManual,
  ClaimOutcomeSendClaimEmail,
} from '@/routes/app/claim-wizard/claim-outcomes';

type ProductHashOutcomePanelProps = {
  resolution: ProductHashResolution;
  isChecking?: boolean;
  onDismiss: () => void;
  onGoToProducts: () => void;
};

/** What this panel shows after a claim action of the member (DEV-756). */
type ClaimStep =
  | { kind: 'resolution' }
  | { kind: 'emailSent'; request?: ClaimRequestViewDto }
  | { kind: 'manualForm' }
  | { kind: 'manualSent'; request?: ClaimRequestViewDto };

const CLAIM_KINDS: ReadonlySet<ProductHashResolution['kind']> = new Set([
  'send_claim_email',
  'claim_pending',
  'claim_manual',
]);

export function ProductHashOutcomePanel({
  resolution,
  isChecking,
  onDismiss,
  onGoToProducts,
}: ProductHashOutcomePanelProps) {
  const claim = useClaimDict();
  const t = claim.product;
  const { can } = usePermissions();
  const canManage = can(Permission.PRODUCTS_MANAGE);
  const intercom = useIntercom();
  const [intercomLoading, setIntercomLoading] = useState(false);
  const [step, setStep] = useState<ClaimStep>({ kind: 'resolution' });
  const [error, setError] = useState<ClaimApiError | null>(null);
  const errorMessage = useClaimErrorMessage();
  const { requestEmail, requestManual } = useClaimRequestActions();
  const { requests, isLoading: requestsLoading } = useClaimRequests();
  const now = useNow(60_000);

  // The live request for this hash, kept fresh by the list query (resends,
  // realtime updates). Falls back to what the last action returned.
  const pendingRequest = useMemo(
    () => findPendingClaimForHash(requests, resolution.hash, now),
    [requests, resolution.hash, now],
  );
  const stepRequest =
    step.kind === 'emailSent' || step.kind === 'manualSent'
      ? step.request
      : undefined;
  // Prefer the list row; once the list shows the request left PENDING
  // (cancelled, decided), stop offering actions on it.
  const listedRow = stepRequest
    ? requests?.find((r) => r.id === stepRequest.id)
    : undefined;
  const liveRequest = stepRequest
    ? listedRow
      ? pendingRequest?.id === stepRequest.id
        ? pendingRequest
        : undefined
      : stepRequest
    : pendingRequest;

  const onActionError = useCallback((err: unknown) => {
    const parsed = readClaimApiError(err);
    if (parsed.code === 'claimRequiresManualVerification') {
      setError(null);
      setStep({ kind: 'manualForm' });
      return;
    }
    setError(parsed);
  }, []);

  const sendClaimEmail = () => {
    setError(null);
    requestEmail.mutate(resolution.hash, {
      onSuccess: (data) =>
        setStep({ kind: 'emailSent', request: data.request }),
      onError: onActionError,
    });
  };

  const sendManualClaim = (note: string) => {
    setError(null);
    requestManual.mutate(
      { hash: resolution.hash, note },
      {
        onSuccess: (request) => setStep({ kind: 'manualSent', request }),
        onError: onActionError,
      },
    );
  };

  const openClaimSupportChat = useCallback(async () => {
    const hash = resolution.hash.trim();
    if (!hash) return;
    setIntercomLoading(true);
    try {
      const opened = await intercom.openNewMessage(
        t.claimedNoEmailPrefill.replace('{{hash}}', hash),
      );
      if (!opened) toast.error(t.error);
    } catch {
      toast.error(t.error);
    } finally {
      setIntercomLoading(false);
    }
  }, [resolution.hash, intercom, t]);

  if (isChecking) {
    return <ClaimOutcomeChecking />;
  }

  // Read-only members see that the product is claimable, without actions.
  if (!canManage && CLAIM_KINDS.has(resolution.kind)) {
    return <ClaimOutcomeNoPermission onScanAnother={onDismiss} />;
  }

  const inlineError =
    error && error.code !== 'claimRequiresManualVerification' ? (
      <ClaimOutcomeInlineError
        title={
          error.code === 'claimResendLimit'
            ? t.resendLimitTitle
            : error.code === 'claimResendCooldown'
              ? t.resendCooldownTitle
              : error.code === 'claimSuperseded'
                ? t.supersededTitle
                : undefined
        }
        message={errorMessage(error)}
      />
    ) : null;

  const withError = (card: React.ReactNode) => (
    <div className="flex flex-col gap-3">
      {card}
      {inlineError}
    </div>
  );

  switch (step.kind) {
    case 'emailSent':
      return withError(
        <ClaimOutcomeEmailSent
          request={liveRequest}
          onGoToProducts={onGoToProducts}
          onError={setError}
        />,
      );
    case 'manualForm':
      return withError(
        <ClaimOutcomeManualForm
          hash={resolution.hash}
          isPending={requestManual.isPending}
          onSubmit={sendManualClaim}
          onContactTeam={() => void openClaimSupportChat()}
          contactLoading={intercomLoading}
        />,
      );
    case 'manualSent':
      return (
        <ClaimOutcomePendingManual
          request={liveRequest}
          onGoToProducts={onGoToProducts}
          onCancelled={onDismiss}
        />
      );
    case 'resolution':
      break;
  }

  switch (resolution.kind) {
    case 'other_workspace':
      return <ClaimOutcomeCantClaim onScanAnother={onDismiss} />;
    case 'assigned_to_other_user':
      return <ClaimOutcomeAssignedToOtherUser onScanAnother={onDismiss} />;
    case 'already_in_workspace':
      return <ClaimOutcomeAlreadyYours onGoToProducts={onGoToProducts} />;
    case 'send_claim_email':
      return withError(
        <ClaimOutcomeSendClaimEmail
          onSubmit={sendClaimEmail}
          isPending={requestEmail.isPending}
        />,
      );
    case 'claim_pending':
      if (resolution.claimMethod === ClaimMethod.MANUAL) {
        return (
          <ClaimOutcomePendingManual
            request={pendingRequest}
            onGoToProducts={onGoToProducts}
            onCancelled={onDismiss}
          />
        );
      }
      if (!pendingRequest) {
        if (requestsLoading) return <ClaimOutcomeChecking />;
        // Not in the list (yet / any more): asking again re-uses or
        // replaces the request server-side.
        return withError(
          <ClaimOutcomeSendClaimEmail
            onSubmit={sendClaimEmail}
            isPending={requestEmail.isPending}
          />,
        );
      }
      return withError(
        <ClaimOutcomePendingEmail
          request={pendingRequest}
          onGoToProducts={onGoToProducts}
          onError={setError}
          onCancelled={onDismiss}
        />,
      );
    case 'claim_manual':
      return withError(
        <ClaimOutcomeManualForm
          hash={resolution.hash}
          isPending={requestManual.isPending}
          onSubmit={sendManualClaim}
          onContactTeam={() => void openClaimSupportChat()}
          contactLoading={intercomLoading}
        />,
      );
    case 'error':
      return <ClaimOutcomeError onScanAnother={onDismiss} />;
    default:
      return null;
  }
}
